// «Отчёт через нейросеть» (MCP): личная ссылка для подключения Claude, ChatGPT, Cursor, Claude Code к редактору (сервер -
// web/server/stoweb/mcpserver.py, API - GET/POST/DELETE /api/me/mcp). ИИ пишет отчёт текстом, оформление по СТО,
// проверку и сборку делает сайт - токены на оформление не тратятся. Ссылка (в ней ключ) показывается один раз (в базе -
// только хэш); новая ссылка отменяет прежнюю. Окно написано для людей, не знающих слов «MCP» и «адрес сервера»: три шага,
// инструкции по вкладкам для каждой нейросети, ссылка сразу подставлена в команды.
import { useEffect, useState, type ReactNode } from 'react';
import type { DialogProps } from '../../core/registry';
import { ctx } from '../../core/ctx';
import { api } from '../../core/api';
import { Btn, Modal, Segmented } from '../../ui/controls';
import { copyText, CopyField } from '../admin/common';
import { askConfirm } from '../../ui/prompts';

interface McpStatus { exists: boolean; hint?: string; created?: number; lastUsed?: number | null; endpoint: string; available: boolean }

const fmtTs = (t: number) => new Date(t * 1000).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

type Client = 'claude' | 'chatgpt' | 'code' | 'cursor' | 'other';
const CLIENT_TABS: { id: Client; label: string }[] = [
  { id: 'claude', label: 'Claude' }, { id: 'chatgpt', label: 'ChatGPT' }, { id: 'code', label: 'Claude Code' },
  { id: 'cursor', label: 'Cursor' }, { id: 'other', label: 'Другая' },
];

const PROMPT = 'Сделай через СТО отчёт по лабораторной работе №1 по дисциплине «Электротехника» на тему «…». ' +
  'Задание и мои результаты - ниже (или в файле). В конце проверь по СТО и дай ссылку на PDF.';

/** Шаг 1-2-3 с кружком-номером. */
function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <section className="au-mcp-step" data-testid={'mcp-step-' + n}>
      <div className="au-mcp-num">{n}</div>
      <div className="au-mcp-body">
        <div className="au-mcp-title">{title}</div>
        {children}
      </div>
    </section>
  );
}

/** Текст просьбы к ИИ: блоком (не полем ввода - длинный текст виден целиком) + «Копировать». */
function CopyBlock({ text, testid }: { text: string; testid: string }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    if (await copyText(text)) { setDone(true); setTimeout(() => setDone(false), 1800); }
    else ctx.toast('Не удалось скопировать - выделите текст и нажмите Ctrl+C', 'warn');
  };
  return (
    <div className="au-mcp-quote">
      <div className="au-mcp-quote-text" data-testid={testid}>{text}</div>
      <Btn icon={done ? 'ok' : 'copy'} label={done ? 'Скопировано' : 'Копировать'} onClick={copy} testid={testid + '-copy'} />
    </div>
  );
}

/** Инструкция для выбранной нейросети; ссылка подставляется, если её только что выдали. */
function ClientHelp({ client, url }: { client: Client; url: string }) {
  const link = url || '<твоя ссылка из шага 1>';
  const paste = url ? <CopyField value={url} testid={'mcp-paste-' + client} /> : <i>твою ссылку из шага 1</i>;
  switch (client) {
    case 'claude': return (
      <>
        <ol className="au-mcp-list">
          <li>Открой <b>claude.ai</b> в браузере (или приложение Claude на компьютере).</li>
          <li>Слева: <b>Customize → Connectors</b> (Настроить → Коннекторы).</li>
          <li>Нажми <b>+ Add → Add custom connector</b>.</li>
          <li>Name: <b>СТО</b>. В поле URL вставь {paste}</li>
          <li>Continue → в Authentication выбери <b>No sign in</b> → <b>Add</b>.</li>
          <li>В новом чате: <b>+</b> слева внизу → <b>Connectors</b> → включи <b>СТО</b>.</li>
        </ol>
        <p className="au-muted small">Работает и на бесплатном Claude (там можно добавить один свой коннектор). На телефоне коннектор появится сам после добавления на сайте.</p>
      </>
    );
    case 'chatgpt': return (
      <>
        <ol className="au-mcp-list">
          <li>Открой <b>chatgpt.com</b> в браузере (в приложении на телефоне так не добавить).</li>
          <li>Аватарка → <b>Settings → Security and login</b> → включи <b>Developer mode</b>.</li>
          <li>Слева <b>Plugins</b> (или Apps) → <b>Add → Create MCP App</b>.</li>
          <li>Name: <b>СТО</b>. Server URL: {paste}</li>
          <li>Authentication: <b>No Auth</b> → галочка «I understand…» → <b>Create</b>.</li>
          <li>В чате: <b>+</b> → <b>Developer mode</b> → выбери <b>СТО</b>.</li>
        </ol>
        <p className="au-muted small">Нужен платный ChatGPT (Plus и выше) - на бесплатном режима разработчика нет. Меню у ChatGPT часто переезжает: если пункта нет, ищи «Developer mode» / «Connectors» в настройках.</p>
      </>
    );
    case 'code': return (
      <>
        <p>Выполни в терминале:</p>
        <CopyField value={`claude mcp add --transport http sto ${link}`} testid="mcp-cmd-code" />
        <p className="au-muted small">Проверить: в Claude Code набери <code>/mcp</code> - там должен быть <b>sto</b>.</p>
      </>
    );
    case 'cursor': return (
      <>
        <ol className="au-mcp-list">
          <li><b>Cursor Settings → Tools &amp; MCP → New MCP Server</b> - откроется файл <code>mcp.json</code>.</li>
          <li>Вставь в него и сохрани:</li>
        </ol>
        <CopyField value={JSON.stringify({ mcpServers: { sto: { url: link } } })} testid="mcp-cmd-cursor" />
      </>
    );
    default: return (
      <p>Подойдёт любая нейросеть или программа, где можно подключить <b>MCP-сервер по ссылке</b> (Streamable HTTP):
        Windsurf, VS Code с Copilot, LibreChat, Claude Desktop и т. п. - вставь туда свою ссылку. DeepSeek, GigaChat, Алиса
        и обычный чат без коннекторов так не умеют.</p>
    );
  }
}

export function McpDialog({ close }: DialogProps<boolean>) {
  const [st, setSt] = useState<McpStatus | null>(null);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [client, setClient] = useState<Client>('claude');
  const load = () => api.get<McpStatus>('/api/me/mcp').then(setSt).catch((e) => ctx.toast(e.message, 'error'));
  useEffect(() => { void load(); }, []);
  const create = async () => {
    if (st?.exists && !(await askConfirm({ title: 'Получить новую ссылку?', text: 'Старая ссылка перестанет работать - её придётся заменить в нейросети.', ok: 'Получить', testid: 'mcp-renew-confirm' }))) return;
    setBusy(true);
    try { const r = await api.post<{ key: string; url: string }>('/api/me/mcp', {}); setUrl(r.url); await load(); }
    catch (e: any) { ctx.toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!(await askConfirm({ title: 'Отключить нейросеть?', text: 'Ссылка перестанет работать, нейросеть потеряет доступ к отчётам.', ok: 'Отключить', danger: true, testid: 'mcp-revoke-confirm' }))) return;
    try { await api.del('/api/me/mcp'); setUrl(''); await load(); ctx.toast('Нейросеть отключена', 'ok'); } catch (e: any) { ctx.toast(e.message, 'error'); }
  };
  return (
    <Modal title="Отчёт через нейросеть" onClose={() => close()} width={680} testid="mcp-dialog"
      footer={<Btn label="Закрыть" onClick={() => close()} />}>
      <div className="au-mcp">
        <p className="au-mcp-lead">Подключи свою нейросеть к сайту один раз - и просто пиши ей «сделай отчёт по лабе». Она пишет
          только текст, а титул, нумерацию, оформление по СТО, проверку и PDF делает сайт, так что лимиты нейросети
          на оформление не тратятся. Готовый отчёт появится в твоём списке отчётов - его можно поправить руками.</p>
        {st && !st.available && <p className="au-warn">На сервере не установлен модуль MCP - сообщите администратору.</p>}

        <Step n={1} title="Получи свою ссылку">
          {url ? (
            <div className="au-mcp-key" data-testid="mcp-new">
              <CopyField value={url} testid="mcp-url" />
              <p className="au-mcp-note">Это как пароль: по ссылке нейросеть работает с твоими отчётами - никому её не кидай.
                Показывается <b>один раз</b>: скопируй и сразу вставь в нейросеть (шаг 2). Потеряешь - просто получи новую.</p>
            </div>
          ) : st?.exists ? (
            <p data-testid="mcp-status">Ссылка уже выдана {st.created ? fmtTs(st.created) : ''}
              {st.lastUsed ? `, нейросеть последний раз заходила ${fmtTs(st.lastUsed)}` : ', нейросеть ещё не подключалась'}.
              Второй раз её не показать: если не сохранил - получи новую, старая перестанет работать.</p>
          ) : st ? <p data-testid="mcp-status">Ссылка ещё не выдана. Нажми кнопку - сайт сделает твою личную ссылку для нейросети.</p>
            : <p className="au-muted">Загрузка…</p>}
          <div className="au-mcp-actions">
            <Btn icon="sparkles" label={st?.exists ? 'Получить новую ссылку' : 'Получить ссылку'} primary={!url} disabled={busy || !st?.available}
              onClick={() => void create()} testid="mcp-create" />
            {st?.exists && <Btn icon="trash" label="Отключить" onClick={() => void revoke()} testid="mcp-revoke" />}
          </div>
        </Step>

        <Step n={2} title="Вставь её в свою нейросеть">
          <Segmented value={client} onChange={setClient} options={CLIENT_TABS} testid="mcp-client" />
          <div className="au-mcp-help" data-testid="mcp-help"><ClientHelp client={client} url={url} /></div>
        </Step>

        <Step n={3} title="Попроси написать отчёт">
          <p>В новом чате (с включённым СТО) напиши, например:</p>
          <CopyBlock text={PROMPT} testid="mcp-prompt" />
          <p className="au-mcp-note">Можно приложить методичку, фото задания и свои измерения. Графики нейросеть не рисует сама -
            отдаёт данные, а сайт строит их по СТО. В конце она пришлёт ссылку на готовый PDF и Word.</p>
        </Step>
      </div>
    </Modal>
  );
}
